// Auth service for FABRIC BUYER authentication ONLY
// This service is exclusively for fabric buyers
import { BaseService, ApiError } from '@/utils/errorHandler';
import { 
  RequestPhoneSignupOtpRequest, 
  CompletePhoneSignupRequest, 
  LoginResponse,
  AuthProviderType,
  UserRoles,
  SignupRequest,
  SignupResponse,
  ReferralCodeOption
} from '../types/auth';

// Using backend-compatible DTOs
export type RequestOtpPayload = RequestPhoneSignupOtpRequest;
export type CompleteSignupPayload = CompletePhoneSignupRequest;
export type CompleteSignupResponse = LoginResponse;

// Response for OTP request
export interface RequestOtpResponse {
  success: boolean;
  expiresIn: number;
  message: string;
}

// Email verification types
export interface RequestEmailVerificationPayload {
  email: string;
  platform: string;
  role: string;
}

export interface VerifyEmailPayload {
  email: string;
  code: string;
  platform: string;
  role: string;
}

export interface VerifyPhonePayload {
  phoneNumber: string;
  otp: string;
  platform: string;
  role: string;
}

export interface RequestPasswordResetPayload {
  email: string;
  platform: string;
  role: string;
}

export interface ResetPasswordPayload {
  email: string;
  code: string;
  newPassword: string;
  platform: string;
  role: string;
}

class AuthService extends BaseService {
  private readonly ALLOWED_ROLE = UserRoles.FabricBuyer
  /**
   * Email/password signup – returns temp token & nextSteps
   */
  async signupWithEmailPassword(payload: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    phoneNumber?: string;
  }): Promise<SignupResponse> {
    // Ensure role & platform are included and phone is normalised
    const body: SignupRequest = {
      ...payload,
      platform: 'fabrics',
      role: UserRoles.FabricBuyer,
      phoneNumber: payload.phoneNumber ? this.normalizePhone(payload.phoneNumber) : undefined,
    } as SignupRequest;

    const response = await this.apiCall<SignupResponse>(
      '/api/v2/auth-2/signup',
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );

    // Store temp token immediately so verification endpoints work
    if (response.token) {
      this.setAuthToken(response.token);
      this.setCurrentRole(UserRoles.FabricBuyer);
    }

    return response;
  }

  /**
   * Email/password login – regular auth token
   */
  async loginWithEmailPassword(email: string, password: string): Promise<LoginResponse> {
    const body = {
      email,
      password,
      platform: 'fabrics',
      role: UserRoles.FabricBuyer,
    };

    const response = await this.debugApiCall<LoginResponse>('/api/v2/auth-2/login', {
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (response.token) {
      this.setAuthToken(response.token);
      this.setCurrentRole(UserRoles.FabricBuyer);
    }
    return response;
  };

  /**
   * Request password reset (email code) - FABRIC BUYERS ONLY
   */
  async requestPasswordReset(payload: RequestPasswordResetPayload): Promise<{ success: boolean; message: string; roles?: string[] }> {
    this.validateFabricBuyerRole(payload.role);
    const body = {
      ...payload,
      platform: 'fabrics',
      role: UserRoles.FabricBuyer
    };
    return this.apiCall<{ success: boolean; message: string; roles?: string[] }>(
      '/api/v2/auth-2/password-reset/request',
      {
        method: 'POST',
        body: JSON.stringify(body)
      }
    );
  }

  /**
   * Reset password with code - FABRIC BUYERS ONLY
   */
  async resetPassword(payload: ResetPasswordPayload): Promise<{ success: boolean; message: string; roles?: string[] }> {
    this.validateFabricBuyerRole(payload.role);
    const body = {
      ...payload,
      platform: 'fabrics',
      role: UserRoles.FabricBuyer
    };
    return this.apiCall<{ success: boolean; message: string; roles?: string[] }>(
      '/api/v2/auth-2/password-reset/reset',
      {
        method: 'POST',
        body: JSON.stringify(body)
      }
    );
  }
  
  constructor() {
    super('FabricBuyerAuthService');
  }
  
  /**
   * Validate that user is a fabric buyer (this app's exclusive role)
   */
  private validateFabricBuyerRole(role: string): void {
    if (role !== this.ALLOWED_ROLE) {
      throw new ApiError(
        'INVALID_ROLE' as any, 
        `This app is exclusively for fabric buyers. Role "${role}" is not allowed.`
      );
    }
  }
  
  /**
   * Add debug logging to apiCall method
   */
  private async debugApiCall<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {

    console.log('   Base URL:', this.baseUrl);
    console.log('   Options:', {
      method: options?.method,
      hasBody: !!options?.body,
    });

    try {
      const result = await this.apiCall<T>(endpoint, options);
      console.log(`✅ API Call Successful: ${endpoint}`);
      return result;
    } catch (error) {
      console.error(`❌ API Call Failed: ${endpoint}`, error);
      throw error;
    }
  }

  /**
   * Request OTP for phone signup - FABRIC BUYERS ONLY
   */
  async requestPhoneSignupOtp(payload: RequestOtpPayload): Promise<RequestOtpResponse> {
    // Validate role before making request
    this.validateFabricBuyerRole(payload.role);
    
    // Ensure platform is always 'fabrics' for this app
    const fabricBuyerPayload = {
      ...payload,
      platform: 'fabrics',
      role: UserRoles.FabricBuyer
    };
    
    return this.debugApiCall<RequestOtpResponse>(
      '/api/v2/auth-2/request-phone-signup-otp',
      {
        method: 'POST',
        body: JSON.stringify(fabricBuyerPayload)
      }
    );
  }

  /**
   * Complete phone signup with OTP verification - FABRIC BUYERS ONLY
   */
  async completePhoneSignup(payload: CompleteSignupPayload): Promise<CompleteSignupResponse> {
    // Validate role before making request
    this.validateFabricBuyerRole(payload.role as string);
    
    // Ensure platform and role are correct for this app
    const fabricBuyerPayload = {
      ...payload,
      platform: 'fabrics',
      role: UserRoles.FabricBuyer
    };
    
    const response = await this.apiCall<CompleteSignupResponse>(
      '/api/v2/auth-2/complete-phone-signup',
      {
        method: 'POST',
        body: JSON.stringify(fabricBuyerPayload)
      }
    );
    
    // Store token and role using BaseService methods
    if (response.token) {
      this.setAuthToken(response.token);
      
      // Validate that the returned role is fabric-buyer
      if (response.currentRole) {
        this.validateFabricBuyerRole(response.currentRole);
        this.setCurrentRole(response.currentRole);
      }
    }
    
    return response;
  }

  /**
   * Request email verification OTP - FABRIC BUYERS ONLY
   */
  async requestEmailVerification(payload: RequestEmailVerificationPayload): Promise<RequestOtpResponse> {
    // Validate role before making request
    this.validateFabricBuyerRole(payload.role);
    
    const fabricBuyerPayload = {
      ...payload,
      platform: 'fabrics',
    } as any;

    // 1. Save email to base profile (required for verification endpoint)
    if (fabricBuyerPayload.email) {
      await this.apiCall('/api/v2/auth-2/base-profile', {
        method: 'PATCH',
        body: JSON.stringify({ email: fabricBuyerPayload.email })
      });
    }

    // 2. Request verification OTP (omit role)
    const { role: _omit, ...body } = fabricBuyerPayload;
    
    return this.apiCall<RequestOtpResponse>(
      '/api/v2/auth-2/request-email-verification',
      {
        method: 'POST',
        body: JSON.stringify(body)
      }
    );
  }

  /**
   * Verify email with OTP - FABRIC BUYERS ONLY
   */
  async verifyEmail(payload: VerifyEmailPayload): Promise<{ success: boolean; message: string }> {
    // Validate role before making request
    this.validateFabricBuyerRole(payload.role);
    
    // Ensure platform is always 'fabrics' for this app
    const fabricBuyerPayload = {
      ...payload,
      platform: 'fabrics'
    };

    const { role: _omitRole, ...body } = fabricBuyerPayload as any;
    
    return this.apiCall<{ success: boolean; message: string }>(
      '/api/v2/auth-2/verify-email',
      {
        method: 'POST',
        body: JSON.stringify(body)
      }
    );
  }

  /**
   * Get complete profile details (base profile) for the current user.
   * Uses /api/v2/auth-2/profile which returns a rich object – we only
   * care about data.baseProfile for display purposes.
   */
  async getProfileDetails(): Promise<{
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
  }> {
    const response = await this.apiCall<any>('/api/v2/auth-2/profile');

    // The endpoint structure is { data: { baseProfile: { ... } } }
    const base = response?.data?.baseProfile || response?.baseProfile || response;

    return {
      _id: base.userId || base._id || '',
      firstName: base.firstName || '',
      lastName: base.lastName || '',
      email: base.email || '',
      phoneNumber: base.phoneNumber || ''
    };
  }

  /**
   * Verify phone with OTP - FABRIC BUYERS ONLY
   */
  private normalizePhone(phone: string): string {
    // Keep leading +, remove everything else that is not digit
    const trimmed = phone.trim();
    if (trimmed.startsWith('+')) {
      return '+' + trimmed.slice(1).replace(/\D/g, '');
    }
    return trimmed.replace(/\D/g, '');
  }

  async requestPhoneVerification(phoneNumber: string): Promise<{ success: boolean; message: string }> {
    const normalized = this.normalizePhone(phoneNumber);
    return this.apiCall<{ success: boolean; message: string }>(
      '/api/v2/auth-2/request-phone-verification',
      {
        method: 'POST',
        body: JSON.stringify({ phoneNumber: normalized, platform: 'fabrics' })
      }
    );
  }

  async verifyPhone(payload: VerifyPhonePayload): Promise<{ success: boolean; message: string }> {
    // Validate role before making request
    this.validateFabricBuyerRole(payload.role);
    
    const cleanedPhone = this.normalizePhone(payload.phoneNumber);
    
    // Ensure platform is always 'fabrics' for this app
    const { role: _omitRole, ...body } = {
      ...payload,
      phoneNumber: cleanedPhone,
      platform: 'fabrics'
    } as any;

    return this.apiCall<{ success: boolean; message: string }>(
      '/api/v2/auth-2/verify-phone',
      {
        method: 'POST',
        body: JSON.stringify(body)
      }
    );
  }

  /**
   * Request OTP for phone login (existing users)
   */
  async requestPhoneLoginOtp(payload: { 
    phoneNumber: string; 
    platform: string; 
    role: string; 
  }) {
    console.log('[DEBUG] Phone Login OTP Request:', {
      phoneNumber: payload.phoneNumber.slice(0, -4) + '****',
      platform: payload.platform,
      role: payload.role
    });

    try {
      const response = await this.apiCall<RequestOtpResponse>(
        '/api/v2/auth-2/request-phone-login-otp',
        {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: {
            'Content-Type': 'application/json',
            'x-platform': 'fabrics',
            'x-app-name': 'fabric-buyer-app'
          }
        }
      );
      
      console.log('[DEBUG] OTP Request Successful:', response);
      return response;
    } catch (error) {
      console.error('[ERROR] Phone Login OTP Request Failed', error);
      throw error;
    }
  }

  /**
   * Check if an email is available for registration
   */
  async checkEmailAvailability(email: string, platform: string, role: string): Promise<{ available: boolean; message: string; errorCode?: string; upgradeAvailable?: boolean; upgradeEndpoint?: string }> {
    return this.apiCall<{ available: boolean; message: string; errorCode?: string; upgradeAvailable?: boolean; upgradeEndpoint?: string }>(
      '/api/v2/auth-2/check-email-availability',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-platform': platform,
          'x-app-name': 'fabric-buyer-app'
        },
        body: JSON.stringify({ email, platform, role })
      }
    );
  }

  /**
   * Check if a phone number is available for registration
   */
  async checkPhoneAvailability(phoneNumber: string, platform: string, role: string): Promise<{ available: boolean; message: string; errorCode?: string; upgradeAvailable?: boolean; upgradeEndpoint?: string }> {
    return this.apiCall<{ available: boolean; message: string; errorCode?: string; upgradeAvailable?: boolean; upgradeEndpoint?: string }>(
      '/api/v2/auth-2/check-phone-availability',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-platform': platform,
          'x-app-name': 'fabric-buyer-app'
        },
        body: JSON.stringify({ phoneNumber, platform, role })
      }
    );
  }

  /**
   * Login with phone + OTP (passwordless)
   */
  async loginWithPhone(payload: { phoneNumber: string; otp: string; platform: string; role: string; }): Promise<LoginResponse> {
    const data = {
      ...payload,
      platform: 'fabrics',
      role: UserRoles.FabricBuyer
    };

    const response = await this.apiCall<LoginResponse>(
      '/api/v2/auth-2/login-phone',
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    );

    // Store token and role
    if (response.token) {
      this.setAuthToken(response.token);
      this.setCurrentRole(UserRoles.FabricBuyer);
    }

    return response;
  }

  /**
   * Persist (partial) profile data for the current fabric buyer.
   * The backend merges the provided fields and returns updated token/user.
   */
  async saveProfileStep(profileData: any): Promise<LoginResponse> {
    return this.apiCall<LoginResponse>(
      '/api/v2/auth-2/complete-profile/fabric-buyer',
      {
        method: 'POST',
        body: JSON.stringify({ 
          role: 'fabric-buyer',
          profileData 
        })
      }
    );
  }

  async getActiveReferralCodesForFabricBuyer(): Promise<ReferralCodeOption[]> {
    const response = await this.apiCall<any>(
      '/api/v2/referral-codes/active?platform=fabric-buyer',
      { method: 'GET' }
    );

    const list = Array.isArray(response) ? response : response?.data;
    if (!Array.isArray(list)) return [];

    return list
      .filter((item) => item && typeof item.code === 'string' && typeof item.displayName === 'string')
      .map((item) => ({
        id: String(item.id || item._id || ''),
        code: String(item.code),
        displayName: String(item.displayName),
      }))
      .filter((item) => Boolean(item.id) && Boolean(item.code) && Boolean(item.displayName));
  }



  /**
   * Fetch profile completion status (lightweight), returns { profileComplete, missingFields[] }
   */
  async getProfileStatus(): Promise<{ profileComplete: boolean; missingFields: string[] }> {
    return this.apiCall<{ profileComplete: boolean; missingFields: string[] }>(
      '/api/v2/auth-2/profile/status',
      { method: 'GET' }
    );
  }

  /**
   * Patch base user profile (email / firstName / lastName).
   */
  async updateBaseProfile(data: { email?: string; firstName?: string; lastName?: string; phoneNumber?: string }): Promise<{ success: boolean; message: string }> {
    const cleaned = data.phoneNumber ? this.normalizePhone(data.phoneNumber) : undefined;
    return this.apiCall<{ success: boolean; message: string }>(
      '/api/v2/auth-2/base-profile',
      {
        method: 'PATCH',
        body: JSON.stringify({ ...data, phoneNumber: cleaned })
      }
    );
  }

  /**
   * Get stored auth token (using BaseService method)
   */
  getStoredAuthToken(): string | null {
    return this.getAuthToken();
  }

  /**
   * Clear stored auth data (using BaseService method)
   */
  logout(): void {
    this.clearAuthData();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  /**
   * Get current user role
   */
  getCurrentUserRole(): string | null {
    return this.getCurrentRole();
  }
}

export const authService = new AuthService(); 
